import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { layersApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { Layer } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

// Query keys
const EMPTY_PARAMS: ListParams = {}

export const layerKeys = {
    all: ['layers'] as const,
    lists: () => [...layerKeys.all, 'list'] as const,
    list: (params: ListParams) => [...layerKeys.lists(), params] as const,
    details: () => [...layerKeys.all, 'detail'] as const,
    detail: (id: number) => [...layerKeys.details(), id] as const,
}

// Layers hooks
export function useLayers(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: layerKeys.list(params || EMPTY_PARAMS),
        queryFn: () => layersApi.list(params),
        select: (response) => response.data, // Extract PaginatedResponse
        enabled: isAuthenticated && !mfaRequired,
        staleTime: 5 * 60 * 1000, // 5 minutes - prevent constant refetches
        refetchOnWindowFocus: false, // Prevent refetch on window focus
        refetchOnMount: false, // Prevent refetch on mount if data is fresh
    })
}

export function useLayer(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: layerKeys.detail(id),
        queryFn: () => layersApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useAddLayer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: { description?: string }) =>
            layersApi.add(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: layerKeys.lists() })
            toast({
                title: 'Layer Added',
                description: `Layer ${response.data.layerID} has been added successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useInsertLayer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: { insertAfterLayerID?: number | null; description?: string }) =>
            layersApi.insert(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: layerKeys.lists() })
            toast({
                title: 'Layer Inserted',
                description: `Layer ${response.data.layerID} has been inserted successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useUpdateLayer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Layer, 'id' | 'createdAt' | 'updatedAt'>> }) =>
            layersApi.update(id, data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: layerKeys.lists() })
            queryClient.invalidateQueries({ queryKey: layerKeys.detail(variables.id) })
            toast({
                title: 'Layer Updated',
                description: `Layer ${response.data.layerID || variables.id} has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteLayer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => layersApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: layerKeys.lists() })
            queryClient.removeQueries({ queryKey: layerKeys.detail(id) })
            toast({
                title: 'Layer Deleted',
                description: 'Layer has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

